/**
 * Get enabled players from a game (filters out disabled and 'left' players)
 * Players must be enabled AND not have status 'left' to be included.
 * Disconnected players are still included as they may reconnect.
 * @param game - The game object (or null)
 * @returns Array of enabled players (excluding those who left)
 */
export function getEnabledPlayers(game) {
    if (!game)
        return [];
    return game.players.filter(p => p.enabled !== false && p.status !== 'left');
}
/**
 * Get connected players from a game (only currently connected players)
 * Players must be enabled AND have status 'connected' to be included.
 * @param game - The game object (or null)
 * @returns Array of enabled, connected players
 */
export function getConnectedPlayers(game) {
    if (!game)
        return [];
    return game.players.filter(p => p.enabled !== false && p.status === 'connected');
}
/**
 * Format a duration in seconds to a human-readable string
 * @param seconds - Duration in seconds
 * @returns Formatted string (e.g., "2m 30s", "45s", "Unlimited")
 */
export function formatDuration(seconds) {
    if (seconds === 0)
        return 'Unlimited';
    if (seconds < 60)
        return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
}
/** A Hide and Seek Teams score (seconds) as a duration, rounded: "0s" for none. */
export function formatHnstScore(seconds) {
    const whole = Math.round(seconds);
    return whole > 0 ? formatDuration(whole) : '0s';
}
/** A share (0–1) as a whole percentage, e.g. "67%"; "—" when there is none. */
export function formatPercent(share) {
    return share === null ? '—' : `${Math.round(share * 100)}%`;
}
/**
 * Competition ranks (1, 1, 3) for values already sorted best first: tied
 * values share a rank.
 */
export function competitionRanks(sortedValues) {
    return sortedValues.map((value) => sortedValues.indexOf(value) + 1);
}
/**
 * How a Hide and Seek Teams round is scored, in a few words (e.g.
 * "Mean-based", "Points-based · 1m per hider found · survivors score too").
 */
export function formatHnstRoundScoring(scoring) {
    if (scoring.mode === 'mean')
        return 'Mean-based';
    return `Points-based · ${formatDuration(scoring.secondsPerFind)} per hider found${scoring.survivorsScore ? ' · survivors score too' : ''}`;
}
/**
 * Status colors for rounds
 */
export const ROUND_STATUS_COLORS = {
    setup: '#6c757d', // Gray - round created/waiting
    hiding: '#ffc107', // Yellow/Amber - hiding phase (HNS)
    seeking: '#28a745', // Green - seeking phase (HNS)
    countdown: '#ffc107', // Yellow/Amber - countdown before race (FPR)
    racing: '#28a745', // Green - race in progress (FPR)
    searching: '#28a745', // Green - searching phase (TRL)
    active: '#28a745', // Green - generic active state
    completed: '#007bff', // Blue
    abandoned: '#dc3545', // Red
    disabled: '#6c757d', // Gray - disabled round
};
/**
 * Get the display color for a round status
 * @param status - The round status
 * @returns Hex color string
 */
export function getRoundStatusColor(status) {
    return ROUND_STATUS_COLORS[status] ?? ROUND_STATUS_COLORS.setup;
}
/**
 * Status labels for rounds
 */
export const ROUND_STATUS_LABELS = {
    setup: 'Setup',
    hiding: 'Hiding',
    seeking: 'Seeking',
    countdown: 'Countdown',
    racing: 'Racing',
    searching: 'Searching',
    active: 'Active',
    completed: 'Completed',
    abandoned: 'Abandoned',
    disabled: 'Disabled',
};
/**
 * Get a human-readable label for a round status
 * @param status - The round status
 * @returns Human-readable status label
 */
export function getRoundStatusLabel(status) {
    return ROUND_STATUS_LABELS[status] ?? 'Unknown';
}
